import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { PersonAddOutlined, PersonRemoveOutlined } from "@mui/icons-material";
import {
  followUnfollow,
  fetchIsFollowing,
  fetchConnections,
} from "../../redux/actions/connections";
import { getStoredCurrentProfile, isShopProfile } from "../../utils/shopProfile";

// -- Import your Authentication Prompt component --
import AuthenticationPrompt from "../Profile/AuthenticationPrompt";

const FollowButton = ({ connectionId, connectionType, className = "" }) => {
  const dispatch = useDispatch();

  // Selectors for the current profile and authentication
  const currentProfile = useSelector((state) => state.user.currentProfile);
  const { user } = useSelector((state) => state.user);
  const { seller, activeSeller, sellers = [] } = useSelector((state) => state.seller);

  const resolvedCurrentProfile = currentProfile || getStoredCurrentProfile() || "User";
  const currentProfileIsShop = isShopProfile(resolvedCurrentProfile);
  const availableShopProfiles = [activeSeller, seller, ...sellers].filter(Boolean);
  const currentShopProfile = currentProfileIsShop
    ? availableShopProfiles.find(
        (shop) =>
          String(shop?._id) === String(resolvedCurrentProfile) ||
          shop?.name === resolvedCurrentProfile
      ) || activeSeller || seller
    : null;
  const canUseShopProfile = Boolean(currentProfileIsShop && currentShopProfile?._id);

  // If user OR seller is logged in, we consider the user "logged in"
  const isLoggedIn = Boolean(user?._id || (canUseShopProfile && currentShopProfile?._id));

  // Determine the ID and type of the current profile
  const currentProfileId = canUseShopProfile ? currentShopProfile?._id : user?._id;
  const currentProfileType = canUseShopProfile ? "Shop" : "User";
  const currentProfileName = canUseShopProfile ? currentShopProfile?.name : "User";
  const isSelfConnection = Boolean(
    currentProfileId &&
      connectionId &&
      currentProfileType === connectionType &&
      String(currentProfileId) === String(connectionId)
  );
  const isOwnUserConnection = Boolean(
    connectionType === 'User' &&
      user?._id &&
      connectionId &&
      String(user._id) === String(connectionId)
  );

  // Track `isFollowing` status for this connectionId
  const isFollowing = useSelector(
    (state) => state.connections.isFollowingById[connectionId]
  );

  // Local state for showing the authentication prompt
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // On mount or when dependencies change, check if the current profile is following the target
  useEffect(() => {
    if (currentProfileId && connectionId && !isSelfConnection) {
      dispatch(
        fetchIsFollowing(currentProfileId, connectionId, currentProfileType, connectionType)
      );
    }
  }, [dispatch, currentProfileId, connectionId, currentProfileType, connectionType, isSelfConnection]);

  /**
   * requireLogin helper
   * If not logged in, show auth prompt; otherwise execute the callback.
   */
  const requireLogin = (action) => {
    if (!isLoggedIn) {
      setShowAuthPrompt(true);
      return;
    }
    action();
  };

  /**
   * Actual follow/unfollow logic.
   * Wrapped in requireLogin so it only triggers if logged in.
   */
  const handleFollowUnfollow = () => {
    if (isSelfConnection || isOwnUserConnection) {
      return;
    }

    if (!currentProfileId) {
      setShowAuthPrompt(true);
      return;
    }

    dispatch(followUnfollow(connectionId, currentProfileType, connectionType, currentProfileName)).then(() => {
      // After follow/unfollow, recheck status
      dispatch(
        fetchIsFollowing(currentProfileId, connectionId, currentProfileType, connectionType)
      );
      dispatch(fetchConnections(connectionId, connectionType, "followers"));
    });
  };

  // -- Modal for Auth Prompt (via React portal) --
  const authPromptModal = showAuthPrompt
    ? ReactDOM.createPortal(
        <div className="auth-modal-backdrop" onClick={() => setShowAuthPrompt(false)}>
          <div
            className="auth-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <AuthenticationPrompt forceVisible />
          </div>
        </div>,
        document.body
      )
    : null;

  if (isSelfConnection || isOwnUserConnection) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => requireLogin(handleFollowUnfollow)}
        className={`add-friend-button-button ${isFollowing ? "following" : "follow"} ${className}`.trim()}
      >
        {isFollowing ? (
          <>
            <PersonRemoveOutlined className="icon" />
            Following
          </>
        ) : (
          <>
            <PersonAddOutlined className="icon" />
            Follow
          </>
        )}
      </button>

      {/* Render Auth Prompt Modal if user/shop is not logged in */}
      {authPromptModal}
    </>
  );
};

export default FollowButton;





